export type JourneyStep = {
  number: string;
  title: string;
  description: string;
};

export type ProductArea = {
  label: string;
  title: string;
  description: string;
  highlights: readonly string[];
  tone: "sage" | "sand" | "peach" | "forest";
};

export type Package = {
  name: "Free" | "Event Pro" | "Conference Pro" | "Conference Plus";
  price: number;
  term: "No paid term" | "12 calendar months";
  description: string;
  features: readonly string[];
  recommended?: boolean;
};

export const journeySteps: readonly JourneyStep[] = [
  { number: "01", title: "Collect", description: "Welcome abstracts, papers, posters, and author details through clear submission journeys." },
  { number: "02", title: "Review", description: "Coordinate fair peer review with scoped assignments, conflict checks, and blind-review boundaries." },
  { number: "03", title: "Decide", description: "Record outcomes, prepare communications, and release decisions when the committee is ready." },
  { number: "04", title: "Register", description: "Keep delegate registration, capacity, payments, and attendance states understandable and separate." },
  { number: "05", title: "Programme", description: "Turn accepted work into a practical schedule across days, rooms, tracks, and formats." },
  { number: "06", title: "Experience", description: "Give participants one place for programmes, posters, networking, badges, and certificates." },
];

export const productAreas: readonly ProductArea[] = [
  {
    label: "Abstract management",
    title: "From first draft to a fair decision.",
    description: "Keep submissions, versions, authors, reviewers, rubrics, and outcomes connected without losing the boundaries each role depends on.",
    highlights: ["Unlimited submissions", "Single- or double-blind review", "Decisions and revision rounds"],
    tone: "sage",
  },
  {
    label: "Conference operations",
    title: "Build the event around the accepted work.",
    description: "Run registration, delegate communications, programme publishing, exports, and event-day operations from the same conference workspace.",
    highlights: ["Delegate registration", "Programme and rooms", "Scoped email and exports"],
    tone: "sand",
  },
  {
    label: "Advanced workflows",
    title: "Complex when you need it. Reviewable always.",
    description: "Design conditional forms, branching workflows, assisted assignments, and automation while keeping every transition visible and versioned.",
    highlights: ["Visual workflow builder", "Advanced review tools", "Reusable roles and reports"],
    tone: "peach",
  },
  {
    label: "Participant experience",
    title: "A conference people can actually navigate.",
    description: "Publish a branded home for programmes, posters, partners, networking, attendance, and certificates that works across devices.",
    highlights: ["Conference website", "Poster gallery and networking", "Badges and certificates"],
    tone: "forest",
  },
];

export const packages: readonly Package[] = [
  {
    name: "Free",
    price: 0,
    term: "No paid term",
    description: "The complete foundation for running a focused academic event.",
    features: ["Unlimited submissions", "Unlimited sequential phases", "Blind review and standard scoring", "Registration and public programme", "Email, exports, and abstract book"],
  },
  {
    name: "Event Pro",
    price: 549,
    term: "12 calendar months",
    description: "For teams that need custom scientific workflows and reusable operations.",
    features: ["Everything in Free", "Conditional forms and custom fields", "Visual workflow builder", "Advanced review and assignment", "Templates, automation, and reports"],
    recommended: true,
  },
  {
    name: "Conference Pro",
    price: 999,
    term: "12 calendar months",
    description: "For a full programme, registration, and branded conference experience.",
    features: ["Everything in Event Pro", "Visual scheduling and conflict checks", "Advanced registration and pricing", "Branded programme and personal agendas", "Restricted and embedded content"],
  },
  {
    name: "Conference Plus",
    price: 1899,
    term: "12 calendar months",
    description: "For participant engagement, partner spaces, and event-day delivery.",
    features: ["Everything in Conference Pro", "Conference website and poster gallery", "Networking and exhibitor spaces", "Badges, QR attendance, and certificates", "Included onboarding session"],
  },
];
